"use client";

import {
	Archive,
	FileText,
	Image as ImageIcon,
	Loader2,
	Music,
	Sparkles,
	Video,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useRecognitionBusy } from "@/components/forms/form-renderer/recognition-busy";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FileUpload } from "@/components/ui/file-upload";
import { useTranslations } from "next-intl";

import type { BaseFieldProps } from "../types";

interface FileUploadSettings {
	accept?: string;
	maxFiles?: number;
	maxSize?: number;
	allowedTypes?: string[];
}

interface UploadedFile {
	id: string;
	name: string;
	size: number;
	type: string;
	url: string;
	signedUrl: string;
}

/**
 * Распознаёт один файл: возвращает заполненные значения или null,
 * если файл не распознан (тогда пробуем следующий).
 */
async function recognizeFileValues(params: {
	fieldId: string;
	file: File;
	formId: string;
	requireValidation: boolean;
}): Promise<Record<string, unknown> | null> {
	const formData = new FormData();
	formData.append("fieldId", params.fieldId);
	formData.append("file", params.file);
	formData.append("formId", params.formId);
	formData.append("requireValidation", String(params.requireValidation));

	try {
		const response = await fetch("/api/recognize", {
			method: "POST",
			body: formData,
		});

		if (!response.ok) {
			return null;
		}

		const result = await response.json();
		const values: Record<string, unknown> = {};

		for (const [fieldId, value] of Object.entries(result.values || {})) {
			if (value !== null && value !== undefined && value !== "") {
				values[fieldId] = value;
			}
		}

		return values;
	} catch {
		return null;
	}
}

export function FileUploadField(props: BaseFieldProps) {

	const t = useTranslations("renderer");	const { field, value, onChange, error, disabled, formId, onRecognizedValues } =
		props;
	const builderMode = props.builderMode;
	// Блокируем область заполнения на время распознавания (t_aba782d3)
	const setRecognitionBusy = useRecognitionBusy()?.setBusy;
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [isRecognizing, setIsRecognizing] = useState(false);
	const [recognitionResult, setRecognitionResult] = useState<{
		message: string;
		type: "success" | "warning";
	} | null>(null);

	const recognitionSettings = field.settings?.recognition;
	const recognitionEnabled =
		!builderMode && recognitionSettings?.enabled && !!onRecognizedValues;

	const getFallbackFormId = () => {
		if (formId) return formId;
		if (typeof window !== "undefined") {
			const path = window.location.pathname;
			const match = path.match(/^\/f\/([^/]+)/);
			return match?.[1] || null;
		}
		return null;
	};

	const getFileUploadSettings = () => field.settings as FileUploadSettings;

	const getMaxFileSize = () => {
		const settings = getFileUploadSettings();
		return settings?.maxSize || 50 * 1024 * 1024;
	};

	const getMaxFiles = () => {
		const settings = getFileUploadSettings();
		return settings?.maxFiles || 10;
	};

	const getAcceptedFileTypes = () => {
		const settings = getFileUploadSettings();
		return (
			settings?.accept ||
			"image/*,application/pdf,video/*,audio/*,text/*,application/zip"
		);
	};

	const getUploadedFiles = () => (Array.isArray(value) ? value : []);

	const getFileIcon = useCallback((type: string) => {
		if (type.startsWith("image/")) return ImageIcon;
		if (type.startsWith("video/")) return Video;
		if (type.startsWith("audio/")) return Music;
		if (type.includes("pdf") || type.includes("document")) return FileText;
		if (type.includes("zip") || type.includes("archive")) return Archive;
		return FileText;
	}, []);

	const formatFileSize = useCallback((bytes: number): string => {
		if (!bytes) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`;
	}, []);

	const createMockFile = (file: File, index: number) => ({
		id: `preview-${Date.now()}-${index}`,
		name: file.name,
		size: file.size,
		type: file.type,
		url: `preview/${file.name}`,
		signedUrl: URL.createObjectURL(file),
	});

	const handleFileUpload = useCallback(
		async (files: File[]) => {
			const actualFormId = getFallbackFormId();

			if (!actualFormId) {
				const mockFiles = files.map(createMockFile);
				const currentFiles = getUploadedFiles();
				onChange([...currentFiles, ...mockFiles]);
				return;
			}

			setIsUploading(true);
			setUploadError(null);

			try {
				const uploadPromises = files.map(async (file) => {
					const formData = new FormData();
					formData.append("file", file);
					formData.append("formId", actualFormId);
					formData.append("fieldId", field.id);

					const response = await fetch("/api/upload", {
						method: "POST",
						body: formData,
					});

					if (!response.ok) {
						const errorData = await response.json();
						throw new Error(errorData.error || t("uploadFailed"));
					}

					const result = await response.json();
					return result.file;
				});

				const newUploadedFiles = await Promise.all(uploadPromises);
				const currentFiles = getUploadedFiles();
				onChange([...currentFiles, ...newUploadedFiles]);

				// Если включено распознавание — запускаем AI-извлечение по всем файлам
				if (recognitionEnabled) {
					await handleRecognizeFiles(files);
				}
			} catch (error) {
				setUploadError(
					error instanceof Error ? error.message : t("uploadFailed")
				);
				throw error;
			} finally {
				setIsUploading(false);
			}
		},
		// handleRecognizeFiles объявлен ниже: в deps его брать нельзя (TDZ),
		// как и в исходной версии с handleRecognizeFile
		[field.id, value, onChange, recognitionEnabled]
	);

	const handleRecognizeFiles = useCallback(
		async (files: File[]) => {
			setIsRecognizing(true);
			setRecognitionResult(null);
			setUploadError(null);
			setRecognitionBusy?.(true);

			const actualFormId = getFallbackFormId();
			if (!actualFormId) {
				setIsRecognizing(false);
				setRecognitionBusy?.(false);
				return;
			}

			try {
				const mergedValues: Record<string, unknown> = {};
				let succeeded = 0;

				// Распознаём ВСЕ файлы поля и объединяем результат (t_aba782d3)
				for (const file of files) {
					const values = await recognizeFileValues({
						fieldId: field.id,
						file,
						formId: actualFormId,
						requireValidation:
							recognitionSettings?.requireValidation ?? true,
					});

					if (!values) {
						// файл не распознался — пробуем остальные
						continue;
					}

					succeeded += 1;
					Object.assign(mergedValues, values);
				}

				if (succeeded === 0) {
					throw new Error(t("recognitionFailed"));
				}

				onRecognizedValues?.(mergedValues);

				const filledCount = Object.keys(mergedValues).length;

				if (filledCount === 0) {
					setRecognitionResult({
						message: t("couldNotExtract"),
						type: "warning",
					});
				} else if (recognitionSettings?.requireValidation) {
					setRecognitionResult({
						message: t("extractedFields", { count: filledCount }),
						type: "success",
					});
				} else {
					setRecognitionResult({
						message: t("autoFilled", { count: filledCount }),
						type: "success",
					});
				}
			} catch (error) {
				setUploadError(
					error instanceof Error ? error.message : t("recognitionFailed")
				);
			} finally {
				setIsRecognizing(false);
				setRecognitionBusy?.(false);
			}
		},
		[
			field.id,
			recognitionSettings?.requireValidation,
			onRecognizedValues,
			setRecognitionBusy,
			getFallbackFormId,
		]
	);

	const handleRemoveUploadedFile = useCallback(
		(fileId: string) => {
			const currentFiles = getUploadedFiles();
			const fileToRemove = currentFiles.find((file) => file.id === fileId);

			if (fileToRemove && fileToRemove.signedUrl.startsWith("blob:")) {
				URL.revokeObjectURL(fileToRemove.signedUrl);
			}

			onChange(currentFiles.filter((file) => file.id !== fileId));
		},
		[value, onChange]
	);

	const handleRemoveButtonClick = (fileId: string) => {
		if (!builderMode) {
			handleRemoveUploadedFile(fileId);
		}
	};

	const isFileImage = (type: string) => type.startsWith("image/");

	const getFileConstraintsText = () => {
		const maxFiles = getMaxFiles();
		const maxSize = getMaxFileSize();
		const settings = getFileUploadSettings();

		return {
			maxFiles,
			maxSize: formatFileSize(maxSize),
			allowedTypes: settings?.allowedTypes,
		};
	};

	useEffect(() => {
		if (value === undefined || value === null || value === "") {
			onChange([]);
		}
	}, []);

	const uploadedFiles = getUploadedFiles();
	const maxFiles = getMaxFiles();
	const maxSize = getMaxFileSize();
	const accept = getAcceptedFileTypes();
	const fallbackFormId = getFallbackFormId();
	const constraints = getFileConstraintsText();

	return (
		<div className="flex flex-col gap-3">
			<Card className="border-0 p-0 shadow-none">
				<CardContent className="p-0">
					<FileUpload
						accept={accept}
						className="bg-transparent"
						disabled={disabled || isUploading || builderMode || isRecognizing}
						maxFiles={maxFiles - uploadedFiles.length}
						maxSize={maxSize}
						multiple={maxFiles > 1}
						onUpload={builderMode ? undefined : handleFileUpload}
						showFileList={false}
						showLimits={false}
						showPreview={false}
					/>
				</CardContent>
			</Card>

			{isRecognizing && (
				<div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
					<Loader2 className="size-4 animate-spin text-primary" />
					<span>
						{t("extractingFields")}
					</span>
				</div>
			)}

			{recognitionResult && !isRecognizing && (
				<Alert
					className={
						recognitionResult.type === "warning"
							? "border-yellow-400 bg-yellow-50 text-yellow-800"
							: "border-primary/30 bg-primary/5"
					}
				>
					<div className="flex items-center gap-2">
						<Sparkles className="size-4" />
						<p className="text-sm">{recognitionResult.message}</p>
					</div>
				</Alert>
			)}

			{uploadError && (
				<Alert variant="destructive">
					<p className="font-medium">{t("uploadError")}</p>
					<p className="text-sm">{uploadError}</p>
				</Alert>
			)}

			{uploadedFiles.length > 0 && (
				<div className="flex flex-col gap-2">
					<h4 className="font-medium text-sm">
						{t("uploadedFiles", { count: uploadedFiles.length })}
					</h4>
					<div className="grid gap-2">
						{uploadedFiles.map((file) => {
							const IconComponent = getFileIcon(file.type);
							const isImage = isFileImage(file.type);

							return (
								<div
									className="flex items-center gap-3 rounded-lg border p-3"
									key={file.id}
								>
									<div className="flex-shrink-0">
										{isImage ? (
											<img
												alt=""
												className="size-10 rounded-md border border-border object-cover"
												src={file.signedUrl}
											/>
										) : (
											<div className="flex size-10 items-center justify-center rounded-md bg-accent">
												<IconComponent className="size-5 text-muted-foreground" />
											</div>
										)}
									</div>

									<div className="min-w-0 flex-1">
										<p className="truncate font-medium text-sm">{file.name}</p>
										<div className="flex items-center gap-2">
											<p className="text-muted-foreground text-xs">
												{formatFileSize(file.size)}
											</p>
											<Badge variant="secondary">{t("uploaded")}</Badge>
										</div>
									</div>

									<Button
										aria-label={t("removeFile", { name: file.name })}
										className="flex-shrink-0"
										disabled={disabled || builderMode}
										onClick={() => handleRemoveButtonClick(file.id)}
										size="icon"
										type="button"
										variant="ghost"
									>
										<X className="size-4" />
									</Button>
								</div>
							);
						})}
					</div>
				</div>
			)}

			<div className="text-muted-foreground text-xs">
				<p>
					{t("maxFiles", { count: constraints.maxFiles, size: constraints.maxSize })}
				</p>
				{constraints.allowedTypes && (
					<p>{t("allowedTypes")} {constraints.allowedTypes.join(", ")}</p>
				)}
			</div>
		</div>
	);
}
